import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, Camera, CheckCircle, CreditCard, Download, FileText, MapPin, Phone, Receipt, UserRound } from 'lucide-react';
import { mapsUrl } from '@/lib/formatters';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (value = 0) => `$${Number(value || 0).toFixed(2)}`;
const photoUrl = (fileId, token) => `${API}/api/photos/file/${fileId}?proof_token=${encodeURIComponent(token || '')}`;
const statusColors = {
  paid: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-sky-100 text-sky-700',
  overdue: 'bg-red-100 text-red-700',
  draft: 'bg-zinc-100 text-zinc-600',
};

function PhotoGrid({ title, photos, testId, token }) {
  return (
    <Card className="border border-zinc-200" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-bold font-heading"><Camera className="h-4 w-4" /> {title}</CardTitle>
      </CardHeader>
      <CardContent>
        {photos.length === 0 ? (
          <p className="py-6 text-center text-sm text-zinc-400" data-testid={`${testId}-empty`}>No photos included.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`${testId}-grid`}>
            {photos.map(photo => (
              <a key={photo.file_id} href={photoUrl(photo.file_id, token)} target="_blank" rel="noopener noreferrer" className="group overflow-hidden rounded-md border border-zinc-200 bg-zinc-100" data-testid={`proof-photo-${photo.file_id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-zinc-100">
                  <img src={photoUrl(photo.file_id, token)} alt={photo.original_filename || title} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105" />
                </div>
                <div className="flex items-center justify-between gap-2 bg-white p-2 text-xs text-zinc-500">
                  <span>{photo.created_at ? new Date(photo.created_at).toLocaleDateString() : ''}</span>
                  {photo.uploaded_by_name && <span className="truncate">{photo.uploaded_by_name}</span>}
                </div>
              </a>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProofPacketPage() {
  const { token } = useParams();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/public/proof/${token}`)
      .then(({ data }) => setPacket(data))
      .catch(() => setError('Proof packet not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const photos = packet?.photos || { before: [], after: [], general: [] };
  const invoice = packet?.invoice || {};
  const hasPayment = invoice.status === 'sent' && invoice.payment_link_url;
  const completionDate = useMemo(() => {
    const raw = packet?.job?.completed_at || packet?.job?.checkout_at;
    return raw ? new Date(raw).toLocaleString() : 'Completed';
  }, [packet]);

  if (loading) {
    return <div className="min-h-screen bg-zinc-100 p-4"><div className="mx-auto mt-12 h-[600px] w-full max-w-5xl animate-pulse rounded-md bg-zinc-200" data-testid="proof-packet-loading" /></div>;
  }
  if (error) {
    return <div className="flex min-h-screen items-center justify-center bg-zinc-100 p-4"><p className="text-sm text-zinc-500" data-testid="proof-packet-error">{error}</p></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-100 px-4 py-6" data-testid="proof-packet-page">
      <div className="mx-auto max-w-5xl space-y-5">
        <section className="overflow-hidden rounded-md border border-zinc-200 bg-white shadow-sm">
          <div className="p-5 sm:p-8" style={{ borderTop: `6px solid ${packet.business.color || '#09090b'}` }}>
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-md text-white" style={{ backgroundColor: packet.business.color || '#09090b' }}>
                  <Briefcase className="h-5 w-5" />
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">Proof of Work</p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight text-zinc-950 font-heading" data-testid="proof-packet-title">{packet.job.title || packet.job.service_category}</h1>
                <p className="mt-1 text-sm text-zinc-500" data-testid="proof-business-name">{packet.business.name}</p>
              </div>
              <div className="rounded-md bg-zinc-50 p-4 text-sm text-zinc-600 sm:min-w-64" data-testid="proof-customer-summary">
                <p className="font-semibold text-zinc-900">{packet.customer.name}</p>
                {packet.job.address && <p className="mt-1 flex gap-1"><MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {packet.job.address}<a href={mapsUrl(packet.job.address)} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-emerald-700" data-testid="proof-packet-map-link" aria-label="Open job address in Google Maps">↗</a></p>}
                <p className="mt-2 text-xs text-zinc-400">Completed: {completionDate}</p>
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border border-zinc-200" data-testid="proof-status-card">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Job Status</p>
              <p className="mt-2 flex items-center gap-2 text-lg font-bold text-emerald-700"><CheckCircle className="h-5 w-5" /> Completed</p>
            </CardContent>
          </Card>
          <Card className="border border-zinc-200" data-testid="proof-photo-count-card">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Photo Proof</p>
              <p className="mt-2 text-lg font-bold text-zinc-900">{photos.before.length} before · {photos.after.length} after</p>
            </CardContent>
          </Card>
          <Card className="border border-zinc-200" data-testid="proof-invoice-status-card">
            <CardContent className="p-4">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Invoice</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-lg font-bold text-zinc-900">{invoice.total ? money(invoice.total) : 'Pending'}</span>
                {invoice.status && <Badge variant="secondary" className={statusColors[invoice.status] || ''}>{invoice.status}</Badge>}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <PhotoGrid title="Before Photos" photos={photos.before} testId="proof-before-photos" token={token} />
          <PhotoGrid title="After Photos" photos={photos.after} testId="proof-after-photos" token={token} />
        </div>

        {(packet.job.completion_notes?.length > 0 || packet.job.technicians?.length > 0) && (
          <Card className="border border-zinc-200" data-testid="proof-completion-notes-card">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base font-bold font-heading"><FileText className="h-4 w-4" /> Completion Notes</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm text-zinc-700">
              {packet.job.completion_notes?.map((note, index) => <p key={index} data-testid={`proof-completion-note-${index}`}>{note}</p>)}
              {packet.job.technicians?.length > 0 && <p className="flex items-center gap-2 text-xs text-zinc-500"><UserRound className="h-3.5 w-3.5" /> Technician: {packet.job.technicians.join(', ')}</p>}
            </CardContent>
          </Card>
        )}

        {invoice.invoice_number && (
          <Card className="border border-zinc-200" data-testid="proof-invoice-card">
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base font-bold font-heading"><Receipt className="h-4 w-4" /> Invoice {invoice.invoice_number}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-hidden rounded-md border border-zinc-200">
                <table className="w-full text-sm">
                  <thead><tr className="bg-zinc-50 text-xs uppercase text-zinc-500"><th className="p-2 text-left">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Total</th></tr></thead>
                  <tbody>{invoice.line_items?.map((item, index) => <tr key={index} className="border-t border-zinc-100"><td className="p-2">{item.description}</td><td className="p-2 text-right">{item.quantity}</td><td className="p-2 text-right font-semibold">{money(item.total)}</td></tr>)}</tbody>
                  <tfoot><tr className="border-t bg-zinc-50"><td colSpan="2" className="p-2 text-right font-bold">Total</td><td className="p-2 text-right text-lg font-bold" data-testid="proof-invoice-total">{money(invoice.total)}</td></tr></tfoot>
                </table>
              </div>
              {hasPayment && (
                <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="proof-pay-button">
                  <a href={invoice.payment_link_url} target="_blank" rel="noopener noreferrer"><CreditCard className="mr-2 h-4 w-4" /> Pay Invoice</a>
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {photos.general.length > 0 && <PhotoGrid title="Additional Photos" photos={photos.general} testId="proof-general-photos" token={token} />}

        <div className="flex flex-col gap-2 pb-6 text-center text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
          <span data-testid="proof-generated-at">Generated {packet.packet.created_at ? new Date(packet.packet.created_at).toLocaleString() : ''}</span>
          <div className="flex justify-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => window.print()} data-testid="proof-print-button"><Download className="mr-1 h-3.5 w-3.5" /> Save / Print</Button>
            {packet.business.contact_phone && <span className="inline-flex items-center gap-1 px-2 py-1"><Phone className="h-3.5 w-3.5" /> {packet.business.contact_phone}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}